import ExpiredToken from '@/components/ExpiredToken'
import Sidenav from '@/components/Sidenav'
import Header from '@/components/Header'
import ChangePasswordDialog from '@/components/ChangePasswordDialog'
import { useBaseStore } from '@/store'
import { Outlet } from 'react-router-dom'
import { ReactNode, useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import { Store } from '@/store/type'
import {
    DndContext,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { useToast } from '@/components/ui/use-toast'
import { OfflineProvider } from '@/context/OfflineContext'
import { useCheckMobile } from '@/hooks/useCheckMobile'
import BottomActionBar from '@/components/BottonNavigator'

interface MainLayoutProps {
    children?: ReactNode
}

const stateSelector = (state: Store) => ({
    isSidenavOpen: state.appConfigSlice.state.isSidenavOpen,
    tokenExpired: state.authSlice.state.tokenExpired,
    accessDenied: state.authSlice.state.accessDenied, 
    resetAccessDenied: state.authSlice.actions.resetAccessDenied,
    isMobile: state.appConfigSlice.state.isMobile
})

const MainLayout = ({ children }: MainLayoutProps) => {
    const { isSidenavOpen, tokenExpired, accessDenied, resetAccessDenied, isMobile } = useBaseStore(stateSelector)
    const [style, setStyle] = useState('')
    useCheckMobile()
    const { toast } = useToast() 
    const sensors = useSensors(
        useSensor(MouseSensor, {}),
        useSensor(TouchSensor, {}),
        useSensor(KeyboardSensor, {})
    )

    useEffect(() => {
        if (accessDenied) {
            toast({
                title: 'Acesso Negado',
                description: 'Você não tem acesso à funcionalidade dessa página!',
                variant: 'destructive',
            })
 
            const timer = setTimeout(() => resetAccessDenied(), 10000)
            return () => clearTimeout(timer)
        }
    }, [accessDenied])

    useEffect(() => {
        if (isMobile) {
            setStyle('w-full ml-0')
        } else {
            const currentStyle = isSidenavOpen
                ? 'ml-sidenav-closed w-container-sidenav-closed sm:ml-sidenav sm:w-container-sidenav'
                : 'ml-sidenav-closed w-container-sidenav-closed'
            setStyle(currentStyle)
        }
    }, [isMobile, isSidenavOpen])
   
    return (
        <DndContext
            collisionDetection={closestCenter}
            modifiers={[restrictToHorizontalAxis]}
            sensors={sensors}
        >
            <div className="w-full h-screen overflow-hidden bg-background text-foreground flex flex-col md:flex-row">
                <div className="hidden md:block">
                    <Sidenav />
                </div>
                <div
                    className={cn(
                        'h-full flex flex-col transition-all duration-200 ease-in-out',
                        style
                    )}
                >
                    <Header />
                    {tokenExpired && <ExpiredToken />}
                    <main className="flex-1 w-full overflow-y-auto md:pb-0 pb-16 bg-background">
                        <OfflineProvider>
                            {children || <Outlet />}
                        </OfflineProvider>
                    </main>
                </div>
                <BottomActionBar />
            </div>
            <ChangePasswordDialog />
        </DndContext>
    )
}

export default MainLayout
